import * as THREE from "three";
import { buildWorld, type WorldHandles } from "./World";
import { buildPlane } from "./PlaneFactory";
import { EffectsSystem } from "./Effects";
import { LaserSystem } from "./Laser";
import { AudioManager } from "./Audio";
import { LocalPredictor } from "./LocalPredictor";
import { bakeMinimap } from "./Minimap";
import { SkyEntities } from "./SkyEntities";
import type { PlaneInput } from "../shared/Physics";
import type { TargetSnapshot, KillRingSnapshot } from "../shared/protocol";
import type { PlayerSnapshot, BulletSnapshot, ExplosionEvent, LaserFireEvent, PlaneSkin, MapType } from "../shared/protocol";
import { LASER } from "../shared/protocol";

interface RemotePlane {
  group: THREE.Group;
  nameTag: THREE.Sprite;
  shieldMesh: THREE.Mesh;
  pos: THREE.Vector3;
  vel: THREE.Vector3;
  quat: THREE.Quaternion;
  posServer: THREE.Vector3;
  quatServer: THREE.Quaternion;
  lastUpdate: number;
  hp: number;
  alive: boolean;
  shieldT: number;
  skin: PlaneSkin;
  prevAlive: boolean;
  laserChargeT: number;
  laserChargeMax: number;
  // Latest aim vector from the server (so we can render the shooter's
  // telegraph along their cursor).
  aimX: number; aimY: number; aimZ: number;
  // Dash state (filled from snapshot)
  dashT: number;
}

export type CameraMode = "follow" | "spectator";

export interface GameCallbacks {
  onLocalUpdate: (s: PlayerSnapshot | null) => void;
  onScoreboard: (rows: PlayerSnapshot[], myId: string) => void;
}

export class Game {
  scene = new THREE.Scene();
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  effects = new EffectsSystem();
  laserSystem = new LaserSystem();
  audio = new AudioManager();
  predictor = new LocalPredictor();
  skyEntities = new SkyEntities();
  world: WorldHandles | null = null;
  minimap: HTMLCanvasElement | null = null;
  myId: string | null = null;
  cameraMode: CameraMode = "spectator";
  // Latest input we last sent to the server — used by the local prediction loop.
  private currentInput: PlaneInput = { pitch: 0, yaw: 0, roll: 0, throttle: 0.6, autoLevel: true };
  private pendingDashTrigger = false;
  // Track whether each remote was charging on the previous tick — we play
  // the charge sound on the rising edge.
  private wasCharging = new Set<string>();
  private wasShielded = new Set<string>();
  private wasDashing = new Set<string>();

  private remotes = new Map<string, RemotePlane>();
  private clock = new THREE.Clock();
  private currentCamPos = new THREE.Vector3(0, 380, 700);
  private currentCamLook = new THREE.Vector3(0, 100, 0);
  private currentCamUp = new THREE.Vector3(0, 1, 0);
  private spectatorT = 0;
  private cb: GameCallbacks;
  private latestPlayers: PlayerSnapshot[] = [];

  constructor(canvas: HTMLCanvasElement, cb: GameCallbacks) {
    this.cb = cb;
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: "high-performance" });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setClearColor(0x9bd2ff);

    this.camera = new THREE.PerspectiveCamera(70, window.innerWidth / window.innerHeight, 0.5, 6000);
    this.camera.position.copy(this.currentCamPos);

    this.scene.background = new THREE.Color(0x9bd2ff);
    this.scene.fog = new THREE.Fog(0x9bd2ff, 800, 2800);
    this.scene.add(this.effects.group);
    this.scene.add(this.laserSystem.group);
    this.scene.add(this.skyEntities.group);

    this.audio.preload();

    window.addEventListener("resize", this.onResize);
    this.onResize();
  }

  private onResize = () => {
    const w = window.innerWidth, h = window.innerHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h, false);
  };

  private worldSeed = 0;
  private worldMapType: MapType | null = null;
  loadWorld(seed: number, mapType: MapType) {
    if (this.world && this.worldSeed === seed && this.worldMapType === mapType) return;
    if (this.world) {
      this.scene.remove(this.world.group);
      this.world = null;
    }
    this.worldSeed = seed;
    this.worldMapType = mapType;
    this.world = buildWorld(seed, mapType);
    this.scene.add(this.world.group);
    // Bake the minimap once per world (cheap, ~50-150 ms).
    this.minimap = bakeMinimap(this.world.sampler, 384, this.world.params, this.world.buildings);
    // Adjust scene atmospherics per map.
    const isMetropolis = mapType === "metropolis";
    const skyColor = isMetropolis ? 0x6e7886 : 0x9bd2ff;
    this.scene.background = new THREE.Color(skyColor);
    this.scene.fog = new THREE.Fog(skyColor, isMetropolis ? 600 : 800, isMetropolis ? 2400 : 2800);
    this.renderer.setClearColor(skyColor);
  }

  setMyId(id: string) { this.myId = id; }
  setCameraMode(mode: CameraMode) { this.cameraMode = mode; }

  // Project the cursor through the camera to get a world-space aim direction
  // *from the player's plane*. Used as the laser fire direction so you shoot
  // at what you're looking at — not just where the plane's nose is pointing.
  computeAimFromCursor(mouseX: number, mouseY: number): THREE.Vector3 | null {
    const me = this.myId ? this.remotes.get(this.myId) : null;
    if (!me) return null;
    const ndcX = (mouseX / window.innerWidth) * 2 - 1;
    const ndcY = -(mouseY / window.innerHeight) * 2 + 1;
    const ray = new THREE.Vector3(ndcX, ndcY, 0.5).unproject(this.camera);
    // Pick a far point along the camera ray, then aim from the plane to it.
    const far = ray.sub(this.camera.position).normalize().multiplyScalar(1500).add(this.camera.position);
    return far.sub(me.pos).normalize();
  }

  // Called by main.ts every input sample so the predictor stays in sync with what
  // we just told the server.
  setLocalInput(input: PlaneInput, dashTriggered: boolean) {
    this.currentInput = input;
    if (dashTriggered) this.pendingDashTrigger = true;
  }
  resetPredictor() {
    this.predictor = new LocalPredictor();
  }

  private latestTargets: TargetSnapshot[] = [];
  private latestRings: KillRingSnapshot[] = [];
  applyState(players: PlayerSnapshot[], bullets: BulletSnapshot[], targets: TargetSnapshot[], rings: KillRingSnapshot[]) {
    this.latestPlayers = players;
    this.latestTargets = targets;
    this.latestRings = rings;
    const now = performance.now();

    const seen = new Set<string>();
    for (const p of players) {
      seen.add(p.id);
      let r = this.remotes.get(p.id);
      if (!r) {
        r = this.spawnRemote(p);
        this.remotes.set(p.id, r);
      }
      const respawned = !r.prevAlive && p.alive;
      const bigJump = r.posServer.distanceTo(new THREE.Vector3(p.x, p.y, p.z)) > 200;
      r.posServer.set(p.x, p.y, p.z);
      r.quatServer.set(p.qx, p.qy, p.qz, p.qw);
      r.vel.set(p.vx, p.vy, p.vz);
      r.lastUpdate = now;
      r.hp = p.hp;
      r.shieldT = p.shieldT;
      r.alive = p.alive;
      r.laserChargeT = p.laserChargeT;
      r.laserChargeMax = p.laserChargeMax || LASER.charge;
      r.aimX = p.aimX; r.aimY = p.aimY; r.aimZ = p.aimZ;
      r.dashT = p.dashT || 0;
      if (respawned || bigJump || (r.pos.x === 0 && r.pos.y === 0 && r.pos.z === 0)) {
        r.pos.copy(r.posServer);
        r.quat.copy(r.quatServer);
      }
      r.prevAlive = p.alive;
      r.group.visible = p.alive;
      r.shieldMesh.visible = p.alive && p.shieldT > 0;

      // Feed the local-player snapshot into the client-side predictor so it can
      // soft-correct any drift relative to its locally-simulated state.
      if (p.id === this.myId) {
        this.predictor.applyServerSnapshot(p, respawned);
      }

      // ===== Audio edges =====
      const charging = p.laserChargeT > 0;
      if (charging && !this.wasCharging.has(p.id)) {
        this.audio.playAt("laser_charge", new THREE.Vector3(p.x, p.y, p.z), { falloff: 600 });
        this.wasCharging.add(p.id);
      } else if (!charging && this.wasCharging.has(p.id)) {
        this.wasCharging.delete(p.id);
      }
      const shielded = p.shieldT > 0;
      if (shielded && !this.wasShielded.has(p.id)) {
        this.audio.playAt("shield_activate", new THREE.Vector3(p.x, p.y, p.z), { falloff: 400 });
        this.wasShielded.add(p.id);
      } else if (!shielded && this.wasShielded.has(p.id)) {
        this.wasShielded.delete(p.id);
      }
      const dashing = (p.dashT || 0) > 0;
      if (dashing && !this.wasDashing.has(p.id)) {
        this.audio.playAt("dash", new THREE.Vector3(p.x, p.y, p.z), { falloff: 400 });
        this.wasDashing.add(p.id);
      } else if (!dashing && this.wasDashing.has(p.id)) {
        this.wasDashing.delete(p.id);
      }
    }
    for (const [id, r] of this.remotes) {
      if (!seen.has(id)) {
        this.scene.remove(r.group);
        this.remotes.delete(id);
      }
    }

    const me = players.find(p => p.id === this.myId) || null;
    this.cb.onLocalUpdate(me);
    this.cb.onScoreboard(players, this.myId || "");
    this.effects.setBullets(bullets.map(b => ({ x: b.x, y: b.y, z: b.z, kind: b.kind })));
  }

  applyEvents(explosions: ExplosionEvent[], kills: { killer: string; victim: string }[], lasers: LaserFireEvent[]) {
    for (const e of explosions) {
      this.effects.spawnExplosion(e.x, e.y, e.z, e.r);
      this.audio.playAt("explosion", new THREE.Vector3(e.x, e.y, e.z), { falloff: 800 });
    }
    for (const l of lasers) {
      const from = new THREE.Vector3(l.ox, l.oy, l.oz);
      const dir = new THREE.Vector3(l.dx, l.dy, l.dz).normalize();
      this.laserSystem.spawnFire(from, dir, l.length, l.blocked);
      this.audio.playAt("laser_fire", from, { falloff: 1200 });
      if (l.blocked) {
        // Play shield-block sound at impact point
        const impact = from.clone().addScaledVector(dir, l.length);
        this.audio.playAt("shield_block", impact, { falloff: 600 });
      }
    }
    for (const k of kills) {
      // Play kill chime if WE got the kill
      const me = this.remotes.get(this.myId || "");
      if (me && this.latestPlayers.find(p => p.id === this.myId)?.name === k.killer) {
        this.audio.play("kill", { volume: 0.8 });
      }
    }
  }

  private spawnRemote(p: PlayerSnapshot): RemotePlane {
    const grp = buildPlane(p.skin);
    grp.position.set(p.x, p.y, p.z);
    grp.quaternion.set(p.qx, p.qy, p.qz, p.qw);
    this.scene.add(grp);

    const nameTag = makeNameTag(p.name);
    nameTag.position.y = 3.5;
    grp.add(nameTag);

    const shieldMesh = new THREE.Mesh(
      new THREE.SphereGeometry(5.5, 12, 8),
      new THREE.MeshBasicMaterial({ color: 0x5cf2ff, transparent: true, opacity: 0.18, wireframe: true }),
    );
    shieldMesh.visible = false;
    grp.add(shieldMesh);

    return {
      group: grp,
      nameTag,
      shieldMesh,
      pos: new THREE.Vector3(p.x, p.y, p.z),
      vel: new THREE.Vector3(p.vx, p.vy, p.vz),
      quat: new THREE.Quaternion(p.qx, p.qy, p.qz, p.qw),
      posServer: new THREE.Vector3(p.x, p.y, p.z),
      quatServer: new THREE.Quaternion(p.qx, p.qy, p.qz, p.qw),
      lastUpdate: performance.now(),
      hp: p.hp, alive: p.alive, shieldT: p.shieldT, skin: p.skin,
      prevAlive: p.alive,
      laserChargeT: p.laserChargeT, laserChargeMax: p.laserChargeMax || LASER.charge,
      aimX: p.aimX, aimY: p.aimY, aimZ: p.aimZ,
      dashT: p.dashT || 0,
    };
  }

  step(dt: number) {
    const now = performance.now();

    // ===== Local plane: client-side prediction =====
    // Run physics locally each frame so the local plane responds instantly to
    // input. Server snapshots arrive at ~20 Hz and softly nudge any drift.
    const me = this.myId ? this.remotes.get(this.myId) : null;
    if (me && this.predictor.alive) {
      this.predictor.step(this.currentInput, dt, this.pendingDashTrigger);
      this.pendingDashTrigger = false;
      this.predictor.correctTowardServer();
      // Use predicted state for our own plane render
      me.pos.copy(this.predictor.pos());
      me.quat.copy(this.predictor.quat());
      me.group.position.copy(me.pos);
      me.group.quaternion.copy(me.quat);
    }

    // ===== Remote planes: extrapolate + soft-correct =====
    for (const [id, r] of this.remotes) {
      if (id === this.myId) continue; // local plane handled above
      if (!r.alive) continue;
      r.pos.addScaledVector(r.vel, dt);
      const age = (now - r.lastUpdate) / 1000;
      const k = Math.min(1, 0.10 + age * 0.6);
      r.pos.lerp(r.posServer, k);
      r.quat.slerp(r.quatServer, Math.min(1, 0.10 + age * 0.8));
      r.group.position.copy(r.pos);
      r.group.quaternion.copy(r.quat);
    }

    // ===== Dash trails (every dashing plane, local + remote) =====
    for (const r of this.remotes.values()) {
      if (!r.alive || r.dashT <= 0) continue;
      // Spawn 2 particles per frame so the trail reads at high speeds.
      r.group.updateMatrixWorld();
      const tail = r.group.localToWorld(new THREE.Vector3(-3.2, 0, 0));
      const fwd = new THREE.Vector3(1, 0, 0).applyQuaternion(r.quat).multiplyScalar(80);
      this.effects.spawnDashTrail(tail, fwd);
      this.effects.spawnDashTrail(tail, fwd);
    }

    // ===== Camera =====
    if (this.cameraMode === "follow") {
      this.followCamera();
    } else {
      this.spectatorCamera(dt);
    }

    // ===== Sky entities (targets + kill rings) =====
    this.skyEntities.setTargets(this.latestTargets, now);
    this.skyEntities.setRings(this.latestRings, now);

    // ===== Laser telegraphs =====
    // Direction = the cursor-aim vector when present (so it points where the
    // shooter is actually looking) — falls back to plane forward otherwise.
    const chargers: { id: string; from: THREE.Vector3; dir: THREE.Vector3; chargeT: number; chargeMax: number; range: number }[] = [];
    for (const [id, r] of this.remotes) {
      if (!r.alive || r.laserChargeT <= 0) continue;
      const fwd = new THREE.Vector3(1, 0, 0).applyQuaternion(r.quat);
      let dir = fwd.clone();
      if (Math.hypot(r.aimX, r.aimY, r.aimZ) > 0.5) {
        const aim = new THREE.Vector3(r.aimX, r.aimY, r.aimZ).normalize();
        // Same forward-cone clamp as the server uses for fire validation.
        if (aim.dot(fwd) > 0.5) dir = aim;
      }
      const from = r.pos.clone().addScaledVector(dir, 4);
      chargers.push({ id, from, dir, chargeT: r.laserChargeT, chargeMax: r.laserChargeMax, range: LASER.range });
    }
    this.laserSystem.updateTelegraphs(chargers);
    this.laserSystem.step(dt);

    // Audio listener tracks the camera
    this.audio.setListener(this.camera.position);

    this.effects.step(dt);
    this.renderer.render(this.scene, this.camera);
  }

  private targetFov = 70;
  private followCamera() {
    const me = this.remotes.get(this.myId || "");
    if (!me || !me.alive || !this.predictor.alive) {
      this.spectatorCamera(0);
      return;
    }
    // FOV punch during dash — gives a "feel" without breaking aim.
    this.targetFov = me.dashT > 0 ? 86 : 70;
    if (Math.abs(this.camera.fov - this.targetFov) > 0.05) {
      this.camera.fov += (this.targetFov - this.camera.fov) * 0.18;
      this.camera.updateProjectionMatrix();
    }
    // Camera follows the locally-predicted state so it reacts instantly to your
    // own input — not the older server snapshot.
    const myPos = this.predictor.pos();
    const myQuat = this.predictor.quat();
    const fwd = new THREE.Vector3(1, 0, 0).applyQuaternion(myQuat);
    const planeUp = new THREE.Vector3(0, 1, 0).applyQuaternion(myQuat);

    const dist = 24, height = 7;
    const desiredPos = new THREE.Vector3()
      .copy(myPos)
      .addScaledVector(fwd, -dist)
      .add(new THREE.Vector3(0, height + Math.max(-3, Math.min(3, fwd.y * 4)), 0));
    this.currentCamPos.lerp(desiredPos, 0.18);

    const lookAhead = new THREE.Vector3().copy(myPos).addScaledVector(fwd, 40);
    this.currentCamLook.lerp(lookAhead, 0.24);

    // Subtle bank feedback (80% world up + 20% plane up)
    const desiredUp = new THREE.Vector3(0, 1, 0).multiplyScalar(0.8).addScaledVector(planeUp, 0.2).normalize();
    this.currentCamUp.lerp(desiredUp, 0.12);

    this.camera.position.copy(this.currentCamPos);
    this.camera.up.copy(this.currentCamUp);
    this.camera.lookAt(this.currentCamLook);

    me.group.updateMatrixWorld();
    const exhaustWorld = me.group.localToWorld(new THREE.Vector3(-3.4, 0, 0));
    this.effects.spawnExhaust(exhaustWorld, fwd.clone().multiplyScalar(50));
  }

  private spectatorCamera(dt: number) {
    this.spectatorT += dt;
    // If there's a target plane to follow, do a cinematic chase. Otherwise orbit.
    const candidates = [...this.remotes.values()].filter(r => r.alive);
    if (candidates.length > 0) {
      // Cycle target every 6 seconds for variety
      const idx = Math.floor(this.spectatorT / 6) % candidates.length;
      const target = candidates[idx];
      const offset = new THREE.Vector3(
        Math.cos(this.spectatorT * 0.4) * 60,
        18 + Math.sin(this.spectatorT * 0.3) * 10,
        Math.sin(this.spectatorT * 0.4) * 60,
      );
      const desired = target.pos.clone().add(offset);
      this.currentCamPos.lerp(desired, 0.04);
      this.currentCamLook.lerp(target.pos, 0.08);
    } else {
      // Slow orbit around origin
      const r = 800;
      const a = this.spectatorT * 0.06;
      this.currentCamPos.lerp(new THREE.Vector3(Math.cos(a) * r, 320, Math.sin(a) * r), 0.05);
      this.currentCamLook.lerp(new THREE.Vector3(0, 100, 0), 0.05);
    }
    this.camera.position.copy(this.currentCamPos);
    this.camera.lookAt(this.currentCamLook);
  }

  start() {
    const loop = () => {
      const dt = Math.min(0.05, this.clock.getDelta());
      this.step(dt);
      requestAnimationFrame(loop);
    };
    requestAnimationFrame(loop);
  }

  dispose() {
    this.renderer.dispose();
    window.removeEventListener("resize", this.onResize);
  }
}

function makeNameTag(text: string): THREE.Sprite {
  const cvs = document.createElement("canvas");
  cvs.width = 256; cvs.height = 64;
  const ctx = cvs.getContext("2d")!;
  ctx.font = "bold 28px ui-sans-serif, system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.lineWidth = 4;
  ctx.strokeStyle = "rgba(0,0,0,0.85)";
  ctx.strokeText(text, cvs.width / 2, cvs.height / 2);
  ctx.fillStyle = "#ffffff";
  ctx.fillText(text, cvs.width / 2, cvs.height / 2);
  const tex = new THREE.CanvasTexture(cvs);
  tex.minFilter = THREE.LinearFilter;
  const mat = new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true });
  const sp = new THREE.Sprite(mat);
  sp.scale.set(8, 2, 1);
  return sp;
}
